import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useToast } from '../hooks/useToast';

const Header = ({ toggleSidebar, isSidebarOpen }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // يمكن إضافة وظيفة البحث هنا لاحقاً
    // على سبيل المثال: البحث في الصفحة الحالية أو فتح نتائج البحث
    console.log('Search query:', query);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      // يمكن إضافة وظيفة البحث عند الضغط على Enter
      console.log('Searching for:', searchQuery);
      // يمكن إضافة redirect أو filter للصفحة الحالية
    }
  };

  const handleImageClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleChangeImage = () => {
    fileInputRef.current?.click();
    setShowProfileMenu(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('يرجى اختيار صورة صحيحة', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('حجم الصورة يجب أن يكون أقل من 5 ميجابايت', 'error');
      return;
    }

    setUploading(true);
    setShowImageModal(true);

    try {
      const formData = new FormData();
      formData.append('profile_picture', file);

      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const response = await fetch(`${baseURL}/users/${user?.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${api.getToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('فشل تحديث الصورة');
      }

      const updatedUser = await response.json();
      
      // Update user in localStorage and context
      localStorage.setItem('user', JSON.stringify(updatedUser));
      showToast('تم تحديث الصورة بنجاح', 'success');
      
      // Reload page to update the image
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error updating image:', error);
      showToast(error.message || 'حدث خطأ في تحديث الصورة', 'error');
    } finally {
      setUploading(false);
      setShowImageModal(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getUserName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email || 'محمد سالم';
  };

  const getUserImage = () => {
    if (user?.profile_picture) {
      return user.profile_picture;
    }
    const name = getUserName();
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=E96A3A&color=fff&size=128`;
  };

  return (
    <header style={{ 
      width: '100%', 
      height: 'auto',
      minHeight: '80px',
      backgroundColor: '#FFFFFF', 
      display: 'flex', 
      flexDirection: 'row',
      alignItems: 'center', 
      justifyContent: 'space-between',
      padding: '20px 32px',
      gap: '24px',
      flexShrink: 0,
      boxSizing: 'border-box',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.03)',
      zIndex: 100,
      direction: 'rtl'
    }}>
      {/* Left Side - Toggle Button and Search */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '20px',
        flex: '1 1 auto'
      }}>
        {/* Toggle Sidebar Button */}
        <button
          onClick={toggleSidebar}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            backgroundColor: isSidebarOpen ? '#E96A3A' : 'rgba(233, 106, 58, 0.1)',
            border: isSidebarOpen ? 'none' : '2px solid rgba(233, 106, 58, 0.2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = isSidebarOpen ? '#F27D72' : 'rgba(233, 106, 58, 0.2)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isSidebarOpen ? '#E96A3A' : 'rgba(233, 106, 58, 0.1)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title={isSidebarOpen ? "إغلاق القائمة" : "فتح القائمة"}
        >
          {isSidebarOpen ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 5l10 10M15 5l-10 10" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M3 11h16M3 6h16M3 16h16" stroke="#E96A3A" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          )}
        </button>

        {/* Search Bar */}
        <div style={{ 
          position: 'relative', 
          width: '100%',
          minWidth: '200px',
          maxWidth: '400px',
          height: '46px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <input
            type="text"
            placeholder="البحث هنا..."
            value={searchQuery}
            onChange={handleSearch}
            onKeyDown={handleSearchSubmit}
            style={{ 
              width: '100%',
              height: '100%',
              borderRadius: '12px',
              border: '1px solid rgba(176, 179, 187, 0.2)',
              backgroundColor: 'rgba(176, 179, 187, 0.08)',
              padding: '10.5px 48px 10.5px 16px',
              fontFamily: 'Cairo, sans-serif',
              fontSize: '14px',
              color: '#2B2F3F',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'all 0.2s'
            }}
            dir="rtl"
            onFocus={(e) => {
              e.target.style.borderColor = '#E96A3A';
              e.target.style.backgroundColor = '#FFFFFF';
              e.target.style.boxShadow = '0px 0px 0px 3px rgba(233, 106, 58, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(176, 179, 187, 0.2)';
              e.target.style.backgroundColor = 'rgba(176, 179, 187, 0.08)';
              e.target.style.boxShadow = 'none';
            }}
          />
          <div style={{ 
            position: 'absolute', 
            right: '16px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            width: '24px',
            height: '24px',
            pointerEvents: 'none'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="#666A7A" strokeWidth="1.5" />
              <path d="m21 21-4.35-4.35" stroke="#666A7A" strokeWidth="1.5" />
            </svg>
          </div>
        </div>
      </div>

      {/* Right Side - User Profile */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px',
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '4px'
        }}>
          <p style={{ 
            color: '#2B2F3F', 
            fontSize: '15px', 
            fontFamily: 'Cairo, sans-serif', 
            fontWeight: 600,
            lineHeight: '20px',
            margin: 0
          }}>
            {getUserName()}
          </p>
          <p style={{ 
            color: '#E96A3A', 
            fontSize: '13px', 
            fontFamily: 'Cairo, sans-serif', 
            fontWeight: 600,
            lineHeight: '20px',
            margin: 0
          }}>
            {user?.userType === 'admin' ? 'أدمن' : user?.userType || 'أدمن'}
          </p>
        </div>
        <div style={{ position: 'relative' }}>
          <div 
            onClick={handleImageClick}
            style={{ 
              width: '44px',
              height: '44px', 
              borderRadius: '12px', 
              border: '2px solid rgba(233, 106, 58, 0.15)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: '#F5F7FA',
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#E96A3A';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(233, 106, 58, 0.15)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <img
              src={getUserImage()}
              alt="Profile"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {uploading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid #FFFFFF',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></div>
              </div>
            )}
          </div>

          {/* Profile Menu */}
          {showProfileMenu && (
            <div style={{
              position: 'absolute',
              left: '0',
              top: 'calc(100% + 8px)',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              minWidth: '180px',
              zIndex: 1000,
              overflow: 'hidden'
            }}>
              <button
                onClick={handleChangeImage}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  textAlign: 'right',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  color: '#2B2F3F',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(233, 106, 58, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                تغيير الصورة
              </button>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Image Upload Modal */}
      {showImageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}
        onClick={() => !uploading && setShowImageModal(false)}
        >
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {uploading ? (
              <>
                <div style={{
                  width: '60px',
                  height: '60px',
                  border: '4px solid rgba(233, 106, 58, 0.2)',
                  borderTop: '4px solid #E96A3A',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 20px'
                }}></div>
                <p style={{
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '16px',
                  color: '#2B2F3F',
                  margin: 0
                }}>
                  جاري رفع الصورة...
                </p>
              </>
            ) : (
              <p style={{
                fontFamily: 'Cairo, sans-serif',
                fontSize: '16px',
                color: '#2B2F3F',
                margin: 0
              }}>
                تم رفع الصورة بنجاح
              </p>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </header>
  );
};

export default Header;
